import { youtubeUrlToEmbedSrc } from '../../utils/noteMediaHtml';

export function countEmbeddedIframes(html: string): number {
  return (html.match(/<iframe\b/gi) ?? []).length;
}

export function normalizeJoditTempHtml(input: string): string {
  if (!input) return input;
  // Jodit sometimes wraps iframes with a custom <jodit> element (data-jodit_iframe_wrapper)
  // which can leak into saved HTML and later interfere with playback/interaction.
  // We convert it to a regular <div> while preserving attributes/styles.
  return input
    .replace(/<jodit\b/gi, '<div')
    .replace(/<\/jodit>/gi, '</div>')
    .replace(/<mark\b[^>]*\btxt-editor-sync-mark\b[^>]*>([\s\S]*?)<\/mark>/gi, '$1');
}

/** One tag per line in HTML source — matches block structure in the preview pane. */
export function formatHtmlSourceForDisplay(html: string): string {
  if (!html) {
    return html;
  }
  return html.replace(/>\s*</g, '>\n<').trim();
}

const NESTABLE_BLOCK_TAGS = new Set(['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'PRE']);

function escapePlainTextForDiv(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\u00a0/g, '&nbsp;');
}

function appendTextNodeBlocks(text: string, out: string[]): void {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  for (const line of lines) {
    if (line.replace(/\u00a0/g, ' ').trim() === '') {
      out.push('<div><br></div>');
    } else {
      out.push(`<div>${escapePlainTextForDiv(line)}</div>`);
    }
  }
}

function blocksFromBrSplitInner(inner: string): string[] {
  const rawParts = inner.split(/<br\s*\/?>/gi);
  const blocks: string[] = [];
  for (let i = 0; i < rawParts.length; i += 1) {
    const trimmed = rawParts[i].trim();
    const isEmpty = !trimmed || trimmed.replace(/&nbsp;/gi, ' ').trim() === '';
    if (isEmpty) {
      const hasMoreNonEmpty = rawParts
        .slice(i + 1)
        .some((part) => part.trim().replace(/&nbsp;/gi, ' ').trim() !== '');
      const isTrailingEmpty = i === rawParts.length - 1;
      // Keep intentional blank lines after Enter (trailing <br>) and between text segments.
      if (hasMoreNonEmpty || isTrailingEmpty) {
        blocks.push('<div><br></div>');
      }
      continue;
    }
    blocks.push(`<div>${trimmed}</div>`);
  }
  return blocks.length > 0 ? blocks : ['<div><br></div>'];
}

function plainTextLinesToDivHtml(text: string): string {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  if (lines.length === 0) {
    return '';
  }
  return lines
    .map((line) => (line.replace(/\u00a0/g, ' ').trim() === '' ? '<div><br></div>' : `<div>${escapePlainTextForDiv(line)}</div>`))
    .join('');
}

function isNestableBlock(el: Element): boolean {
  return NESTABLE_BLOCK_TAGS.has(el.tagName);
}

function isEffectivelyEmptyBlock(el: HTMLElement): boolean {
  const clone = el.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('br').forEach((br) => br.remove());
  const text = (clone.textContent ?? '').replace(/\u00a0/g, ' ').trim();
  const hasMedia = clone.querySelector('iframe, img, video, audio');
  return text === '' && !hasMedia;
}

function leafBlockToDivs(el: HTMLElement): string[] {
  if (isEffectivelyEmptyBlock(el)) {
    return ['<div><br></div>'];
  }

  if (el.children.length === 0) {
    const blocks: string[] = [];
    appendTextNodeBlocks(el.textContent ?? '', blocks);
    return blocks.length > 0 ? blocks : ['<div><br></div>'];
  }

  const inner = el.innerHTML.trim();
  if (!/<(?:div|p|h[1-6]|li|blockquote|pre|iframe)\b/i.test(inner)) {
    const parts = inner.split(/<br\s*\/?>/gi);
    if (parts.length > 1) {
      return blocksFromBrSplitInner(inner);
    }
  }

  return [`<div>${inner}</div>`];
}

function collapseConsecutiveEmptyBlocks(blocks: string[]): string[] {
  const out: string[] = [];
  for (const block of blocks) {
    const isEmpty = block === '<div><br></div>';
    const prevEmpty = out.length > 0 && out[out.length - 1] === '<div><br></div>';
    if (isEmpty && prevEmpty) {
      continue;
    }
    out.push(block);
  }
  return out;
}

function hasDirectBlockDescendant(el: HTMLElement): boolean {
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType !== Node.ELEMENT_NODE) {
      continue;
    }
    const childEl = child as Element;
    if (childEl.tagName === 'IFRAME' || isNestableBlock(childEl)) {
      return true;
    }
  }
  return false;
}

function collectTopLevelBlocks(root: ParentNode): string[] {
  const out: string[] = [];

  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (text.length > 0) {
        appendTextNodeBlocks(text, out);
      }
      continue;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      continue;
    }

    const el = node as HTMLElement;
    if (el.tagName === 'IFRAME') {
      out.push(el.outerHTML);
      continue;
    }

    if (isNestableBlock(el)) {
      if (hasDirectBlockDescendant(el)) {
        out.push(...collectTopLevelBlocks(el));
        continue;
      }
      if (isEffectivelyEmptyBlock(el)) {
        out.push('<div><br></div>');
        continue;
      }
      out.push(...leafBlockToDivs(el));
      continue;
    }

    out.push(`<div>${el.outerHTML}</div>`);
  }

  return out;
}

/** Flatten orphan text and nested blocks so each preview line maps to one top-level div. */
export function structureNormalizeTxtEditorHtml(html: string): string {
  const value = (html || '').trim();
  if (!value) {
    return '';
  }

  if (typeof document === 'undefined') {
    return value;
  }

  if (!/<[a-z][\s\S]*>/i.test(value)) {
    return plainTextLinesToDivHtml(value);
  }

  const container = document.createElement('div');
  container.innerHTML = value;
  const blocks = collapseConsecutiveEmptyBlocks(collectTopLevelBlocks(container));
  return blocks.length > 0 ? blocks.join('') : '';
}

/** Plain text → one &lt;div&gt; per line; &lt;div&gt;&lt;br&gt;&lt;/div&gt; only for blank lines in the paste. */
export function plainTextToEditorDivHtml(text: string): string {
  return plainTextLinesToDivHtml(text);
}

export function normalizeTxtEditorHtml(input: string): string {
  const cleaned = normalizeJoditTempHtml(input || '');
  const structured = structureNormalizeTxtEditorHtml(cleaned);
  return formatHtmlSourceForDisplay(structured);
}

function isInterTagWhitespace(html: string, index: number): boolean {
  if (!/\s/.test(html[index] ?? '')) {
    return false;
  }
  let cursor = index;
  while (cursor < html.length && /\s/.test(html[cursor])) {
    cursor += 1;
  }
  return cursor < html.length && html[cursor] === '<';
}

export function replaceYoutubeUrlsWithIframes(input: string): string {
  if (!input) return input;
  // Replace each YT URL occurrence with an iframe block.
  // Keep it HTML-friendly because TXT mode intentionally stores HTML snippets too.
  const ytUrlRegex = /\bhttps?:\/\/(?:www\.)?(?:youtu\.be\/[^\s<]+|youtube\.com\/[^\s<]+|m\.youtube\.com\/[^\s<]+)\b/gi;
  return input.replace(ytUrlRegex, (match) => {
    const embedSrc = youtubeUrlToEmbedSrc(match);
    if (!embedSrc) return match;
    return `<iframe src="${embedSrc}" width="400" height="345" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen" allowfullscreen></iframe>`;
  });
}

export const TXT_EDITOR_SYNC_HIGHLIGHT = 'txt-editor-sync';

/** Length in source HTML of a named/numeric entity that renders as one character. */
export function htmlEntitySourceLengthAt(html: string, index: number): number | null {
  if (html[index] !== '&') {
    return null;
  }
  const semi = html.indexOf(';', index);
  if (semi === -1 || semi - index > 14) {
    return null;
  }
  const entity = html.slice(index, semi + 1);
  if (
    entity === '&nbsp;' ||
    entity === '&amp;' ||
    entity === '&lt;' ||
    entity === '&gt;' ||
    entity === '&quot;' ||
    entity === '&apos;' ||
    entity === '&#39;' ||
    /^&#\d+;$/i.test(entity) ||
    /^&#x[0-9a-f]+;$/i.test(entity)
  ) {
    return entity.length;
  }
  return null;
}

/** Trim bounds for the same HTML string shown in the contentEditable preview. */
export function previewHtmlTrimBounds(html: string): { start: number; end: number } {
  let start = 0;
  while (start < html.length && /\s/.test(html[start])) {
    start += 1;
  }
  let end = html.length;
  while (end > start && /\s/.test(html[end - 1])) {
    end -= 1;
  }
  return { start, end };
}

/** Textarea indices are on `description`; preview uses trimmed normalized HTML. */
export function descriptionIndexToPreviewHtmlIndex(description: string, index: number): number {
  const normalized = normalizeTxtEditorHtml(description || '');
  const { start, end } = previewHtmlTrimBounds(normalized);
  const adjusted = index - start;
  return Math.max(0, Math.min(adjusted, Math.max(0, end - start)));
}

const COPYABLE_TOKEN_MAX_LEN = 2048;

export function htmlEntityToVisibleChar(html: string, index: number): string {
  const len = htmlEntitySourceLengthAt(html, index);
  if (!len) {
    return html[index] ?? '';
  }
  const entity = html.slice(index, index + len);
  if (entity === '&nbsp;') return '\u00a0';
  if (entity === '&amp;') return '&';
  if (entity === '&lt;') return '<';
  if (entity === '&gt;') return '>';
  if (entity === '&quot;') return '"';
  if (entity === '&apos;' || entity === '&#39;') return "'";
  const dec = entity.match(/^&#(\d+);$/);
  if (dec) return String.fromCodePoint(Number(dec[1]));
  const hex = entity.match(/^&#x([0-9a-f]+);$/i);
  if (hex) return String.fromCodePoint(parseInt(hex[1], 16));
  return '?';
}

export function isValidCopyableToken(html: string, openIdx: number, closeIdx: number): boolean {
  if (closeIdx === -1 || closeIdx - openIdx > COPYABLE_TOKEN_MAX_LEN + 4) {
    return false;
  }
  const inner = html.slice(openIdx + 2, closeIdx);
  return !inner.includes('<');
}

/** htmlStarts[v] = HTML index of visible character at offset v; same rules as DOM TreeWalker. */
export function buildVisibleTextMap(html: string): { htmlStarts: number[]; text: string } {
  const htmlStarts: number[] = [];
  let text = '';
  let i = 0;
  let inTag = false;

  while (i < html.length) {
    if (!inTag && html[i] === '[' && html[i + 1] === '[') {
      const closeIdx = html.indexOf(']]', i + 2);
      if (closeIdx !== -1 && isValidCopyableToken(html, i, closeIdx)) {
        const tokenEnd = closeIdx + 2;
        for (let j = i; j < tokenEnd; j++) {
          htmlStarts.push(j);
          text += html[j];
        }
        i = tokenEnd;
        continue;
      }
    }

    const ch = html[i];
    if (ch === '<') {
      inTag = true;
      i += 1;
      continue;
    }
    if (inTag) {
      if (ch === '>') {
        inTag = false;
      }
      i += 1;
      continue;
    }

    if (isInterTagWhitespace(html, i)) {
      while (i < html.length && /\s/.test(html[i])) {
        i += 1;
      }
      continue;
    }

    const entityLen = htmlEntitySourceLengthAt(html, i);
    if (entityLen) {
      htmlStarts.push(i);
      text += htmlEntityToVisibleChar(html, i);
      i += entityLen;
      continue;
    }

    htmlStarts.push(i);
    text += ch;
    i += 1;
  }

  return { htmlStarts, text };
}

export function htmlSourceOffsetToVisibleTextOffset(html: string, sourceOffset: number): number {
  const { htmlStarts } = buildVisibleTextMap(html);
  for (let v = 0; v < htmlStarts.length; v++) {
    if (htmlStarts[v] >= sourceOffset) {
      return v;
    }
  }
  return htmlStarts.length;
}

export function visibleTextOffsetToHtmlSourceOffset(html: string, targetOffset: number): number {
  const { htmlStarts } = buildVisibleTextMap(html);
  if (targetOffset <= 0) {
    return 0;
  }
  if (targetOffset >= htmlStarts.length) {
    return html.length;
  }
  return htmlStarts[targetOffset];
}

export function getVisibleTextFromRoot(root: HTMLElement): string {
  let text = '';
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, previewTextNodeFilter(root));
  let node: Node | null;
  while ((node = walker.nextNode())) {
    text += (node as Text).data;
  }
  return text;
}

export function findVisibleOffsetsForSelectedText(
  domVisible: string,
  selectedText: string,
  hintStart: number,
): { start: number; end: number } | null {
  if (!selectedText) {
    return null;
  }
  const from = Math.max(0, hintStart - selectedText.length);
  let idx = domVisible.indexOf(selectedText, from);
  if (idx === -1) {
    idx = domVisible.indexOf(selectedText);
  }
  if (idx === -1) {
    return null;
  }
  return { start: idx, end: idx + selectedText.length };
}

export function previewHtmlIndexToDescriptionIndex(description: string, previewIndex: number): number {
  const normalized = normalizeTxtEditorHtml(description || '');
  const { start, end } = previewHtmlTrimBounds(normalized);
  return Math.min(start + previewIndex, end);
}

export function getPointVisibleOffset(root: HTMLElement, container: Node, offset: number): number {
  const point = document.createRange();
  try {
    point.setStart(container, offset);
    point.collapse(true);
  } catch {
    return 0;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, previewTextNodeFilter(root));
  let total = 0;
  let textNode: Node | null;
  while ((textNode = walker.nextNode())) {
    const tn = textNode as Text;
    const nodeRange = document.createRange();
    nodeRange.selectNodeContents(tn);
    if (point.compareBoundaryPoints(Range.START_TO_START, nodeRange) < 0) {
      return total;
    }
    if (point.compareBoundaryPoints(Range.START_TO_END, nodeRange) > 0) {
      total += tn.data.length;
      continue;
    }
    const innerRange = document.createRange();
    innerRange.setStart(tn, 0);
    innerRange.setEnd(container, offset);
    return total + innerRange.toString().length;
  }
  return total;
}

export function getRangeVisibleTextOffsets(
  contentRoot: HTMLElement,
  range: Range,
): { start: number; end: number } | null {
  if (!contentRoot.contains(range.startContainer) || !contentRoot.contains(range.endContainer)) {
    return null;
  }
  const start = getPointVisibleOffset(contentRoot, range.startContainer, range.startOffset);
  const end = getPointVisibleOffset(contentRoot, range.endContainer, range.endOffset);
  return { start: Math.min(start, end), end: Math.max(start, end) };
}

/** Map HTML source textarea selection to a DOM range in the preview pane. */
export function resolvePreviewRangeFromHtmlSourceSelection(
  description: string,
  descSelStart: number,
  descSelEnd: number,
  contentRoot: HTMLElement,
): Range | null {
  const descStart = Math.min(descSelStart, descSelEnd);
  const descEnd = Math.max(descSelStart, descSelEnd);
  const slice = description.slice(descStart, descEnd);
  if (!slice) {
    return null;
  }

  const { text: sliceVisible } = buildVisibleTextMap(slice);
  const selectedVisible = sliceVisible || slice;
  const domVisible = getVisibleTextFromRoot(contentRoot);
  const hint = resolvePreviewVisibleSyncRange(description, descStart, descEnd)?.textStart ?? 0;
  const match = findVisibleOffsetsForSelectedText(domVisible, selectedVisible, hint);
  if (!match) {
    return null;
  }
  return findVisibleTextRangeInRoot(contentRoot, match.start, match.end);
}

export function resolvePreviewVisibleSyncRange(
  description: string,
  descSelStart: number,
  descSelEnd: number,
): { textStart: number; textEnd: number } | null {
  const normalized = normalizeTxtEditorHtml(description || '');
  const { start: trimStart, end: trimEnd } = previewHtmlTrimBounds(normalized);
  const html = normalized.slice(trimStart, trimEnd) || '<p><br></p>';
  const previewSelStart = descriptionIndexToPreviewHtmlIndex(description, descSelStart);
  const previewSelEnd = descriptionIndexToPreviewHtmlIndex(description, descSelEnd);
  const textStart = htmlSourceOffsetToVisibleTextOffset(html, previewSelStart);
  const textEnd = htmlSourceOffsetToVisibleTextOffset(html, previewSelEnd);
  if (textEnd < textStart) {
    return null;
  }
  return { textStart, textEnd };
}

export function resolveVisibleRangeFromDomSelection(
  contentRoot: HTMLElement,
  range: Range,
): { start: number; end: number; text: string } | null {
  const text = range.toString();
  if (!text) {
    return null;
  }
  const domVisible = getVisibleTextFromRoot(contentRoot);
  const hint = getRangeVisibleTextOffsets(contentRoot, range)?.start ?? 0;
  const match = findVisibleOffsetsForSelectedText(domVisible, text, hint);
  if (match) {
    return { ...match, text };
  }
  const start = hint;
  const end = Math.min(start + text.length, domVisible.length);
  if (domVisible.slice(start, end) === text) {
    return { start, end, text };
  }
  return null;
}

/** Map preview DOM selection to HTML source textarea indices. */
export function resolveHtmlSourceRangeFromPreviewSelection(
  description: string,
  contentRoot: HTMLElement,
  range: Range,
): { start: number; end: number } | null {
  const resolved = resolveVisibleRangeFromDomSelection(contentRoot, range);
  if (!resolved?.text) {
    return null;
  }

  const source = description || '';
  const selectedText = resolved.text;
  const hintHtml = visibleTextOffsetToHtmlSourceOffset(source, resolved.start);
  const occurrences = findAllSourceIndicesOf(source, selectedText);

  if (occurrences.length > 0) {
    const start = pickClosestSourceIndex(occurrences, hintHtml);
    return { start, end: start + selectedText.length };
  }

  const { text: sourceVisible } = buildVisibleTextMap(source);
  const match = findVisibleOffsetsForSelectedText(sourceVisible, selectedText, resolved.start);
  if (!match) {
    return null;
  }

  const htmlStart = visibleTextOffsetToHtmlSourceOffset(source, match.start);
  if (source.slice(htmlStart, htmlStart + selectedText.length) === selectedText) {
    return { start: htmlStart, end: htmlStart + selectedText.length };
  }

  return {
    start: htmlStart,
    end: visibleTextOffsetToHtmlSourceOffset(source, match.end),
  };
}

function findAllSourceIndicesOf(haystack: string, needle: string): number[] {
  if (!needle) {
    return [];
  }
  const indices: number[] = [];
  let from = 0;
  while (from <= haystack.length) {
    const idx = haystack.indexOf(needle, from);
    if (idx < 0) {
      break;
    }
    indices.push(idx);
    from = idx + 1;
  }
  return indices;
}

function pickClosestSourceIndex(indices: number[], hint: number): number {
  let best = indices[0];
  let bestDist = Math.abs(best - hint);
  for (let i = 1; i < indices.length; i += 1) {
    const dist = Math.abs(indices[i] - hint);
    if (dist < bestDist) {
      best = indices[i];
      bestDist = dist;
    }
  }
  return best;
}

const TEXTAREA_MIRROR_STYLE_PROPS = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'letterSpacing',
  'textTransform',
  'wordSpacing',
  'textIndent',
  'boxSizing',
  'borderLeftWidth',
  'borderRightWidth',
  'paddingLeft',
  'paddingRight',
  'lineHeight',
  'whiteSpace',
  'wordBreak',
  'overflowWrap',
] as const;

function measureTextareaOffsetTop(ta: HTMLTextAreaElement, offset: number): { top: number; height: number } {
  const div = document.createElement('div');
  const style = div.style;
  const cs = window.getComputedStyle(ta);
  for (const prop of TEXTAREA_MIRROR_STYLE_PROPS) {
    style[prop] = cs[prop];
  }
  style.position = 'absolute';
  style.visibility = 'hidden';
  style.pointerEvents = 'none';
  style.top = '0';
  style.left = '-9999px';
  style.width = `${ta.clientWidth}px`;
  style.whiteSpace = 'pre-wrap';
  style.wordBreak = 'break-word';
  style.overflowWrap = 'break-word';

  const safeOffset = Math.max(0, Math.min(offset, (ta.value ?? '').length));
  const value = ta.value ?? '';
  div.textContent = value.slice(0, safeOffset);
  const marker = document.createElement('span');
  marker.textContent = value.charAt(safeOffset) || '\u200b';
  div.appendChild(marker);

  document.body.appendChild(div);
  const top = marker.offsetTop;
  const height = marker.offsetHeight || parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.2 || 18;
  document.body.removeChild(div);
  return { top, height };
}

/** Vertical band for a textarea selection (used for sync highlight overlay). */
export function measureTextareaSelectionBand(
  ta: HTMLTextAreaElement,
  start: number,
  end: number,
): { top: number; height: number } {
  const len = (ta.value ?? '').length;
  const selStart = Math.max(0, Math.min(start, len));
  const selEnd = Math.max(selStart, Math.min(end, len));
  const startPos = measureTextareaOffsetTop(ta, selStart);
  const endMeasure = selEnd > selStart ? selEnd - 1 : selEnd;
  const endPos = measureTextareaOffsetTop(ta, endMeasure);
  const top = Math.min(startPos.top, endPos.top);
  const bottom = Math.max(startPos.top + startPos.height, endPos.top + endPos.height);
  return { top, height: Math.max(bottom - top, startPos.height) };
}

/** Scroll HTML source textarea only when the selection is outside the visible area. */
export function scrollTextareaToSelection(
  ta: HTMLTextAreaElement,
  start: number,
  end?: number,
) {
  const len = (ta.value ?? '').length;
  const selStart = Math.max(0, Math.min(start, len));
  const selEnd = Math.max(selStart, Math.min(end ?? selStart, len));

  const startPos = measureTextareaOffsetTop(ta, selStart);
  const endPos = measureTextareaOffsetTop(ta, selEnd);
  const top = Math.min(startPos.top, endPos.top);
  const bottom = Math.max(startPos.top + startPos.height, endPos.top + endPos.height);
  const padding = 8;
  const viewTop = ta.scrollTop;
  const viewBottom = ta.scrollTop + ta.clientHeight;

  let nextScrollTop = ta.scrollTop;
  if (top < viewTop + padding) {
    nextScrollTop = Math.max(0, top - padding);
  } else if (bottom > viewBottom - padding) {
    nextScrollTop = Math.max(0, bottom - ta.clientHeight + padding);
  }

  if (nextScrollTop !== ta.scrollTop) {
    ta.scrollTop = nextScrollTop;
  }
}

export function findVisibleTextRangeInRoot(root: HTMLElement, textStart: number, textEnd: number): Range | null {
  if (textEnd <= textStart) return null;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, previewTextNodeFilter(root));

  let offset = 0;
  let startNode: Text | null = null;
  let startOffset = 0;
  let endNode: Text | null = null;
  let endOffset = 0;

  let node: Node | null;
  while ((node = walker.nextNode())) {
    const textNode = node as Text;
    const len = textNode.data.length;
    if (!startNode && offset + len > textStart) {
      startNode = textNode;
      startOffset = textStart - offset;
    }
    if (offset + len >= textEnd) {
      endNode = textNode;
      endOffset = textEnd - offset;
      break;
    }
    offset += len;
  }

  if (!startNode || !endNode) return null;

  try {
    const range = document.createRange();
    range.setStart(startNode, Math.min(Math.max(0, startOffset), startNode.data.length));
    range.setEnd(endNode, Math.min(Math.max(0, endOffset), endNode.data.length));
    return range.collapsed ? null : range;
  } catch {
    return null;
  }
}

export function clearTxtEditorSyncCssHighlight() {
  try {
    if (typeof CSS !== 'undefined' && CSS.highlights) {
      CSS.highlights.delete(TXT_EDITOR_SYNC_HIGHLIGHT);
    }
  } catch {
    /* ignore */
  }
  clearTxtEditorSyncMark(
    document.querySelector('[data-txt-preview-content]'),
  );
}

export function clearTxtEditorSyncMark(root?: ParentNode | null) {
  const scope = root ?? document;
  scope.querySelectorAll('mark.txt-editor-sync-mark').forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark);
    }
    parent.removeChild(mark);
  });
}

/** Neon sync highlight (DOM mark — ::highlight() ignores font-size / padding). */
export function wrapRangeWithTxtEditorSyncMark(range: Range): boolean {
  const mark = document.createElement('mark');
  mark.className = 'txt-editor-sync-mark';
  mark.setAttribute('data-txt-editor-sync', '1');
  try {
    range.surroundContents(mark);
    return true;
  } catch {
    try {
      const fragment = range.extractContents();
      mark.appendChild(fragment);
      range.insertNode(mark);
      return true;
    } catch {
      return false;
    }
  }
}

export function previewTextNodeFilter(root: HTMLElement): NodeFilter {
  return {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent || !root.contains(parent)) return NodeFilter.FILTER_REJECT;
      if (parent.closest('button')) return NodeFilter.FILTER_REJECT;
      if (parent.closest('mark.txt-editor-sync-mark')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  };
}

